import { sendTemplate } from '@/features/atendimento/api';
import { listTemplates, type TemplateRow } from '@/features/configuracoes/api';
import type { LibraryMedia } from '@/features/media-library/api';
import {
  type FormatoDoCabecalho,
  MidiaDoCabecalhoPicker,
} from '@/features/media-library/MidiaDoCabecalhoPicker';
import { ApiError } from '@/shared/lib/api-client';
import { Button } from '@/shared/components/ui/Button';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface Props {
  onClose: () => void;
  /**
   * Filtra templates pelo canal (WABA do whatsapp_number). Sem isso, picker
   * mostraria templates de OUTRAS WABAs do tenant — Meta não permite enviar
   * template de WABA A num canal da WABA B.
   */
  whatsappNumberId?: number;
  /**
   * Ticket id pro envio. Quando ausente, modo "preview only" — botão fica
   * desabilitado. Quando presente, habilita envio via POST /messages.
   */
  ticketId?: number;
}

/**
 * Story 4.7 + 7.6 — picker de templates HSM aprovados.
 *
 * Quando recebe `ticketId`, envia via POST /messages com type='template' →
 * backend resolve template + publica em meta.send.template. Bolha aparece
 * via realtime emit (sem optimistic — body renderizado vem do backend).
 *
 * Sem `ticketId` (raro hoje), opera só como visualizador.
 */
export function TemplatePickerModal({ onClose, whatsappNumberId, ticketId }: Props) {
  useEscapeClose(true, onClose);
  const q = useQuery({
    queryKey: ['templates', 'approved', whatsappNumberId ?? null],
    queryFn: () => listTemplates('approved', whatsappNumberId),
  });
  const [selected, setSelected] = useState<TemplateRow | null>(null);
  const [variables, setVariables] = useState<Record<string, string>>({});
  // Mídia do cabeçalho deste envio (galeria ou arquivo do computador).
  const [headerMedia, setHeaderMedia] = useState<LibraryMedia | null>(null);
  const formatoDoCabecalho = selected ? cabecalhoDeMidia(selected) : null;

  const sendMut = useMutation({
    mutationFn: () => {
      if (!ticketId || !selected) throw new Error('missing ticket_id or template');
      return sendTemplate({
        ticket_id: ticketId,
        template_id: selected.id,
        ...(selected.variableCount > 0 && { template_variables: variables }),
        ...(headerMedia && { header_media_id: headerMedia.id }),
      });
    },
    onSuccess: () => {
      toast.success('Template enviado', {
        description: 'Aguardando confirmação do WhatsApp…',
      });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        toast.error(err.problem.title, {
          description: err.problem.detail ?? 'Erro ao enviar template.',
        });
      } else {
        toast.error('Erro ao enviar template');
      }
    },
  });

  // Variáveis preenchidas? (todas as N inputs com algum valor)
  const allVarsFilled =
    !selected ||
    selected.variableCount === 0 ||
    Array.from({ length: selected.variableCount }, (_, i) => String(i + 1)).every(
      (k) => (variables[k] ?? '').trim().length > 0,
    );

  // Cabeçalho de mídia exige o arquivo em todo envio.
  const midiaOk = formatoDoCabecalho == null || headerMedia != null;
  const canSend = !!ticketId && !!selected && allVarsFilled && midiaOk && !sendMut.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex h-[600px] w-[680px] max-w-full flex-col rounded-lg bg-n-0 shadow-lg">
        <header className="flex items-center justify-between border-n-200 border-b px-5 py-3">
          <h3 className="font-bold text-[16px]">Escolher template HSM</h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-n-500 hover:bg-n-100"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </header>

        <div className="grid flex-1 grid-cols-2 overflow-hidden">
          <div className="overflow-y-auto border-n-200 border-r p-3">
            {q.isLoading && <Loader2 size={18} className="mx-auto animate-spin text-n-400" />}
            {!q.isLoading && (q.data ?? []).length === 0 && (
              <div className="px-3 py-12 text-center text-[12px] text-n-500">
                Nenhum template aprovado. Sincronize em /configuracoes/templates.
              </div>
            )}
            {(q.data ?? []).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setSelected(t);
                  setVariables({});
                  setHeaderMedia(null);
                }}
                className={`mb-1.5 w-full rounded-md border px-3 py-2 text-left text-[12px] transition ${
                  selected?.id === t.id
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-n-200 hover:bg-n-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono font-semibold text-n-800">{t.name}</span>
                  <span className="rounded-pill bg-n-100 px-1.5 text-[10px] text-n-600">
                    {t.language}
                  </span>
                </div>
                <div className="mt-0.5 text-[10px] text-n-500">
                  {t.category} · {t.variableCount} variáveis
                </div>
              </button>
            ))}
          </div>

          <div className="overflow-y-auto p-4">
            {!selected && (
              <div className="text-center text-[12px] text-n-500">
                Selecione um template à esquerda.
              </div>
            )}
            {selected && (
              <div>
                <h4 className="mb-2 font-semibold text-[13px] text-n-800">{selected.name}</h4>
                <div className="whitespace-pre-wrap rounded-md bg-n-50 p-3 text-[12px] text-n-800">
                  {renderPreview(selected, variables)}
                </div>
                {/* Mídia do cabeçalho antes das variáveis — o cabeçalho abre a mensagem. */}
                {formatoDoCabecalho && (
                  <div className="mt-3">
                    <MidiaDoCabecalhoPicker
                      type={formatoDoCabecalho}
                      selectedId={headerMedia?.id ?? null}
                      selectedLabel={headerMedia?.label ?? null}
                      selectedMime={headerMedia?.mime_type ?? null}
                      onSelect={(m) => setHeaderMedia(m)}
                      onClear={() => setHeaderMedia(null)}
                    />
                  </div>
                )}
                {selected.variableCount > 0 && (
                  <div className="mt-3 flex flex-col gap-2">
                    <span className="font-semibold text-[11px] uppercase tracking-[0.05em] text-n-500">
                      Variáveis
                    </span>
                    {Array.from({ length: selected.variableCount }, (_, i) => i + 1).map((n) => (
                      <label key={n} className="flex flex-col gap-1">
                        <span className="font-mono text-[11px] text-n-600">{`{{${n}}}`}</span>
                        <input
                          value={variables[String(n)] ?? ''}
                          onChange={(e) =>
                            setVariables({ ...variables, [String(n)]: e.target.value })
                          }
                          className="rounded-md border border-n-200 px-2 py-1 text-[12px]"
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <footer className="flex items-center justify-end gap-2 border-n-200 border-t px-5 py-3">
          <Button variant="ghost" onClick={onClose} disabled={sendMut.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => sendMut.mutate()}
            disabled={!canSend}
            title={
              !ticketId
                ? 'Sem ticket selecionado'
                : !selected
                  ? 'Selecione um template'
                  : !allVarsFilled
                    ? 'Preencha todas as variáveis'
                    : !midiaOk
                      ? 'Escolha a mídia do cabeçalho'
                      : undefined
            }
          >
            {sendMut.isPending ? (
              <>
                <Loader2 size={13} className="animate-spin" /> Enviando…
              </>
            ) : (
              'Enviar template'
            )}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function renderPreview(template: TemplateRow, variables: Record<string, string>): string {
  // Concatena `text` dos componentes e substitui `{{N}}`.
  const text = (template.components as Array<{ text?: string }>)
    .map((c) => c.text ?? '')
    .filter(Boolean)
    .join('\n\n');
  return text.replace(/\{\{(\d+)\}\}/g, (_, n) => variables[n] || `{{${n}}}`);
}

/** Formato do cabeçalho de MÍDIA do template, ou null (texto/sem cabeçalho). */
function cabecalhoDeMidia(template: TemplateRow): FormatoDoCabecalho | null {
  const comps = (template.components ?? []) as Array<{ type?: string; format?: string }>;
  const header = comps.find((c) => (c.type ?? '').toUpperCase() === 'HEADER');
  const fmt = (header?.format ?? 'TEXT').toUpperCase();
  if (fmt === 'IMAGE') return 'image';
  if (fmt === 'VIDEO') return 'video';
  if (fmt === 'DOCUMENT') return 'document';
  return null;
}
